import { Component, OnInit, Inject, PLATFORM_ID } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-signup-form',
  standalone: true,
  imports: [ReactiveFormsModule, HttpClientModule, CommonModule],
  templateUrl: './signup-form.html',
  styleUrl: './signup-form.scss',
})
export class SignupForm implements OnInit {
  signupForm: FormGroup;
  isLoading = false;
  errorMessage = '';

  // Email validation regex matching the backend validation
  // Allows: letters, numbers, dots, hyphens, underscores, and plus signs
  // Examples: john.doe@example.com, user+tag@mail-server.com
  private emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.signupForm = this.fb.group({
      email: ['', [Validators.required, Validators.pattern(this.emailRegex)]],
    });
  }

  ngOnInit() {
    // Only reset form state on browser platform to avoid SSR issues
    if (isPlatformBrowser(this.platformId)) {
      this.resetFormState();
    }
  }

  onSubmit() {
    if (
      this.signupForm.valid &&
      !this.isLoading &&
      isPlatformBrowser(this.platformId)
    ) {
      this.isLoading = true;
      this.errorMessage = '';

      const email = this.signupForm.get('email')?.value;

      // Call the validation service signup endpoint
      this.http.post(`${this.getApiBaseUrl()}/signup`, { email }).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          console.log('Signup successful:', response);

          // Navigate to welcome page after successful signup
          this.router.navigate(['/welcome']);
        },
        error: (error) => {
          this.isLoading = false;
          console.error('Signup error:', error);

          // Handle different error types
          if (error.status === 400) {
            if (error.error?.code === 'INVALID_EMAIL_FORMAT') {
              this.errorMessage = 'Please enter a valid email address';
            } else if (error.error?.code === 'INVALID_INPUT') {
              this.errorMessage = 'Email is required';
            } else {
              this.errorMessage =
                error.error?.message || 'Invalid input provided';
            }
          } else if (error.status === 409) {
            this.errorMessage = 'This email address is already registered';
          } else if (error.status === 0 || !error.status) {
            this.errorMessage =
              'Unable to connect to the server. Please check your connection and try again.';
          } else {
            this.errorMessage =
              error.error?.message ||
              'An unexpected error occurred. Please try again.';
          }
        },
      });
    } else {
      // Mark all fields as touched to show validation errors
      this.signupForm.markAllAsTouched();
    }
  }

  private getApiBaseUrl(): string {
    return environment.apiUrl;
  }

  private resetFormState() {
    this.isLoading = false;
    this.errorMessage = '';
  }

  // Helper method to check if email field has errors
  getEmailError(): string {
    const emailControl = this.signupForm.get('email');
    if (emailControl?.errors && emailControl.touched) {
      if (emailControl.errors['required']) {
        return 'Email is required';
      }
      if (emailControl.errors['pattern']) {
        return 'Please enter a valid email address';
      }
    }
    return '';
  }
}
